import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { storage } from '@/lib/storage';

/**
 * Tenant store — owns the active tenant code (sent as the `X-Tenant` header on every request).
 * Persisted via `storage` so it survives reloads and is available to the axios interceptor.
 */
interface TenantStoreValue {
  tenantCode: string | null;
  isResolved: boolean;
  setTenantCode: (code: string | null) => void;
  clearTenant: () => void;
}

const TenantContext = createContext<TenantStoreValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenantCode, setCode] = useState<string | null>(storage.tenantCode);

  const setTenantCode = useCallback((code: string | null) => {
    storage.setTenant(code);
    setCode(code);
  }, []);

  const clearTenant = useCallback(() => {
    storage.setTenant(null);
    setCode(null);
  }, []);

  const value = useMemo<TenantStoreValue>(
    () => ({ tenantCode, isResolved: !!tenantCode, setTenantCode, clearTenant }),
    [tenantCode, setTenantCode, clearTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenantStore() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenantStore must be used within a TenantProvider');
  return ctx;
}
